import { z } from 'zod'
import { getRequiredFieldNames } from './requiredFields'

// WHY: Shared between frontend and backend, so a format can neither be submitted nor published
// via direct API calls without passing the same checks as in the editor. Error messages are
// frontend translation keys.

type UnknownRecord = Record<string, unknown>

export interface FormatSubmitValidationData {
    format?: UnknownRecord
    company?: UnknownRecord
    institutions?: UnknownRecord
    events?: UnknownRecord[]
    jobPostings?: UnknownRecord[]
}

export type FormatInstitutionLike = { function?: string | null }

export const hasOrganizingInstitution = (
    format: { ownInstitutionFunction?: string | null },
    institutions?: FormatInstitutionLike[] | null
): boolean =>
    format.ownInstitutionFunction === 'ORGANIZED_AND_CONDUCTED_BY' ||
    (institutions ?? []).some((institution) => institution.function === 'ORGANIZED_BY')

export interface FormatSubmitSource {
    company?: unknown
    ownInstitutionFunction?: string | null
    institutions?: FormatInstitutionLike[] | null
    events?: unknown[] | null
    job_offers?: unknown[] | null
}

export const toFormatSubmitValidationData = (
    format: FormatSubmitSource
): FormatSubmitValidationData => {
    const formatCompany =
        format.company && typeof format.company === 'object'
            ? (format.company as UnknownRecord)
            : {}

    return {
        format: { ...format },
        // WHY: Company validation must use the format-company relation as source of truth.
        company: formatCompany,
        institutions: {
            ownInstitutionFunction: format.ownInstitutionFunction,
            hasOrganizingInstitution: hasOrganizingInstitution(format, format.institutions),
            institutions: format.institutions,
        },
        events: (format.events as UnknownRecord[] | null | undefined) || [],
        jobPostings: (format.job_offers as UnknownRecord[] | null | undefined) || [],
    }
}

const getNestedValue = (obj: UnknownRecord, path: string): unknown => {
    return path.split('.').reduce((current: unknown, key) => {
        if (typeof current !== 'object' || current === null) return undefined
        return (current as UnknownRecord)[key]
    }, obj as unknown)
}

const isFieldFilled = (value: unknown): boolean => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string' && value.trim() === '') return false
    if (Array.isArray(value) && value.length === 0) return false

    // Booleans and numbers have no "empty" equivalent in this domain; any concrete value counts as filled.
    if (typeof value === 'boolean' || typeof value === 'number') return true

    if (typeof value === 'object' && !Array.isArray(value)) {
        if ('selected' in value) {
            // selected === false means the user explicitly opted out of this widget; no sub-fields are required.
            if (value.selected === false) return true

            // User opted in (selected === true or default), so all dependent sub-fields must be non-empty.
            const otherKeys = Object.keys(value).filter((k) => k !== 'selected')
            return otherKeys.every((key) => isFieldFilled((value as UnknownRecord)[key]))
        }

        // For generic complex objects (e.g. address), presence of any sub-value is sufficient here;
        // individual field completeness is enforced by the form's own field-level validation.
        return Object.values(value).some((nestedValue) => isFieldFilled(nestedValue))
    }

    return true
}

const hasMissingRequiredField = (data: UnknownRecord, requiredFields: string[]): boolean => {
    return requiredFields.some((fieldName) => !isFieldFilled(getNestedValue(data, fieldName)))
}

const BaseFormatSubmitSchema = z.object({
    format: z.record(z.string(), z.unknown()).default({}),
    company: z.record(z.string(), z.unknown()).default({}),
    institutions: z
        .looseObject({
            institutions: z
                .array(z.record(z.string(), z.unknown()), {
                    error: 'frontend.format.validation.requiredInstitutionsFields',
                })
                .default([]),
        })
        .default({ institutions: [] }),
    events: z.array(z.record(z.string(), z.unknown())).default([]),
    jobPostings: z.array(z.record(z.string(), z.unknown())).default([]),
})

export const FormatSubmitValidationSchema = BaseFormatSubmitSchema.superRefine((data, ctx) => {
    if (hasMissingRequiredField(data.format, getRequiredFieldNames('format', 'submit', {}))) {
        ctx.addIssue({
            code: 'custom',
            message: 'frontend.format.validation.requiredFormatFields',
            path: ['format'],
        })
    }

    if (hasMissingRequiredField(data.company, getRequiredFieldNames('company', 'submit', {}))) {
        ctx.addIssue({
            code: 'custom',
            message: 'frontend.format.validation.requiredCompanyFields',
            path: ['company'],
        })
    }

    // Every format needs an organizing institution (own org as "Veranstaltet und durchgeführt von",
    // or a dedicated "Veranstaltet von" entry) in addition to the always-present conducting org.
    const ownInstitutionFunction = data.institutions.ownInstitutionFunction as string | undefined
    const institutions = data.institutions.institutions as FormatInstitutionLike[]

    if (
        hasMissingRequiredField(
            data.institutions,
            getRequiredFieldNames('institutions', 'submit', {})
        ) ||
        !hasOrganizingInstitution({ ownInstitutionFunction }, institutions)
    ) {
        ctx.addIssue({
            code: 'custom',
            message: 'frontend.format.validation.requiredInstitutionsFields',
            path: ['institutions'],
        })
    }

    // At least one event must exist because a format without events cannot be published or reviewed.
    if (data.events.length === 0) {
        ctx.addIssue({
            code: 'custom',
            message: 'frontend.format.validation.atLeastOneEventRequired',
            path: ['events'],
        })
        return
    }

    const locationFieldCandidates = ['state', 'address', 'postCode', 'city']
    const dateFieldCandidates = ['startDate', 'endDate']
    const eventLocationFields = getRequiredFieldNames('events', 'submit', {}).filter((fieldName) =>
        locationFieldCandidates.includes(fieldName)
    )
    // Dates follow the explicit dateNotDefined opt-out and must be checked separately.
    const eventBaseFields = getRequiredFieldNames('events', 'submit', {}).filter(
        (fieldName) =>
            !locationFieldCandidates.includes(fieldName) && !dateFieldCandidates.includes(fieldName)
    )
    const eventDateFields = getRequiredFieldNames('events', 'submit', {}).filter((fieldName) =>
        dateFieldCandidates.includes(fieldName)
    )
    const locationRequiredModalities = ['ON_SITE', 'HYBRID']

    const hasIncompleteEvent = data.events.some((event) => {
        if (hasMissingRequiredField(event, eventBaseFields)) return true

        // The toggle intentionally allows publishing events before their dates are known.
        if (event.dateNotDefined !== true && hasMissingRequiredField(event, eventDateFields)) {
            return true
        }

        // Physical address (address, city) is only relevant for in-person or hybrid events;
        // fully online events have no location to provide.
        if (
            eventLocationFields.length > 0 &&
            locationRequiredModalities.includes(event.modality as string) &&
            event.addressNotDefined !== true
        ) {
            return hasMissingRequiredField(event, eventLocationFields)
        }
        return false
    })

    if (hasIncompleteEvent) {
        ctx.addIssue({
            code: 'custom',
            message: 'frontend.format.validation.requiredEventFields',
            path: ['events'],
        })
    }
})

export interface FormatSubmitValidationResult {
    valid: boolean
    errorKeys: string[]
}

export const validateFormatSubmitData = (
    data: FormatSubmitValidationData
): FormatSubmitValidationResult => {
    const result = FormatSubmitValidationSchema.safeParse(data)

    if (result.success) {
        return {
            valid: true,
            errorKeys: [],
        }
    }

    return {
        valid: false,
        errorKeys: [...new Set(result.error.issues.map((issue) => issue.message))],
    }
}
